// src/pages/geoscape-hub/usage/usage-page.tsx
import { useIdentity } from "contexts/identity-context";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { StandardPageLayout } from "components/layouts/standard-page-layout";
import { BarChart3 } from "lucide-react";
import dayjs from "dayjs";
import { useState } from "react";
import { UsageDateRangePicker } from "components/usage/usage-date-range-picker";
import { UsageCard } from "components/usage/usage-card";
import { EditOverageCard } from "components/usage/edit-overage-card";
import { UsageGraph } from "components/usage/usage-graph";
import { UsageGraphSkeleton } from "components/usage/usage-graph-skeleton";
import {
  UsageCreditByTimeTable,
  UsageTable,
} from "components/usage/usage-tables";
import {
  useFetchApiKeys,
  useFetchSubscription,
  useFetchUsageGraph,
} from "components/usage/fetch-hooks";
import { unixDurationToDateRange } from "components/usage/utils-usage";
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from "components/ui/popover";
import { Button } from "@/components/ui/button";

export const UsagePage = () => {
  const [identityState] = useIdentity();

  const [startTime, setStartTime] = useState(
    dayjs().subtract(29, "day").unix() * 1000,
  );
  const [endTime, setEndTime] = useState(dayjs().endOf("day").unix() * 1000);
  const [label, setLabel] = useState(
    unixDurationToDateRange(startTime, endTime).toString(),
  );

  const subscriptionQuery = useFetchSubscription(identityState);
  const apiKeysQuery = useFetchApiKeys(identityState);

  const usageQuery = useFetchUsageGraph(
    identityState,
    startTime,
    endTime,
    !!subscriptionQuery.data,
  );

  const isFreemium: boolean =
    subscriptionQuery.isSuccess &&
    !!subscriptionQuery.data.activeSubscription &&
    subscriptionQuery.data.activeSubscription.plan.free_plan;

  const isLoading =
    subscriptionQuery.isPending ||
    (usageQuery.isEnabled && usageQuery.isPending) ||
    apiKeysQuery.isPending;
  const isError =
    subscriptionQuery.isError || usageQuery.isError || apiKeysQuery.isError;
  const isSuccess =
    subscriptionQuery.isSuccess &&
    usageQuery.isSuccess &&
    apiKeysQuery.isSuccess;

  return (
    <StandardPageLayout
      pageTitle={{
        icon: BarChart3,
        title: "Usage",
        description: "Monitor your API credit consumption and usage patterns.",
      }}
    >
      <ReactQueryErrorWrapper
        queries={[subscriptionQuery, usageQuery]}
        mutations={[]}
      />
      <div className="flex gap-2 justify-end">
        <UsageDateRangePicker
          isFreemium={isFreemium}
          label={label}
          setStartTime={setStartTime}
          setEndTime={setEndTime}
          setLabel={setLabel}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <UsageCard
          isError={isError}
          isLoading={isLoading}
          isSuccess={isSuccess}
          totalCredits={usageQuery.data?.appCreditsData.total_credits}
        />
        <EditOverageCard />
      </div>

      <UsageGraphSkeleton isLoading={isLoading} />
      {isSuccess && (
        <UsageGraph
          startTime={startTime}
          endTime={endTime}
          isLoading={isLoading}
          isError={isError}
          isSuccess={isSuccess}
          usageQuery={usageQuery}
        />
      )}

      <UsageGraphSkeleton isLoading={isLoading} />
      {isSuccess && (
        <UsageCreditByTimeTable
          startTime={startTime}
          endTime={endTime}
          isLoading={isLoading}
          isError={isError}
          isSuccess={isSuccess}
          usageQuery={usageQuery}
        />
      )}

      <UsageGraphSkeleton isLoading={isLoading} />
      {isSuccess && (
        <UsageTable
          startTime={startTime}
          endTime={endTime}
          isLoading={isLoading}
          isError={isError}
          isSuccess={isSuccess}
          usageQuery={usageQuery}
          apiKeysQuery={apiKeysQuery}
        />
      )}
    </StandardPageLayout>
  );
};
